import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {SegmentedControl} from '@/components/segmented-control'
import {formatMonthName} from '@/lib/risk'
import type {FuentePlan} from '@/hooks/sgsst/plan/use-actions'
import {StatCard} from '@/components/section-card'
import {formatCopRangeOf, type Cop} from '@/lib/money'
import {tope} from '@/lib/campos-texto'

export type PlanOrder = 'agrupado' | 'target_date' | 'status'
export type PlanSortMode = 'estandar' | 'riesgo'
export type PlanStatusFilter = 'todas' | 'pendientes'

const ORDER_LABEL_KEY: Record<PlanOrder, string> = {
  agrupado: 'agrupado',
  target_date: 'vencimiento',
  status: 'estado',
}

/** Sin este chip, una lista recortada desde la gráfica se leería como incompleta. */
function FiltroChip({label, onClear, testId}: {label: string; onClear: () => void; testId: string}) {
  const {t} = useTranslation()
  return (
    <span
      className="tone-green inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium"
      data-testid={testId}>
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={t('plan.mando.quitarFiltro')}
        className="leading-none opacity-70 hover:opacity-100"
        data-testid={`${testId}-quitar`}>
        ✕
      </button>
    </span>
  )
}

export function PlanMando({
  search,
  onSearch,
  order,
  onOrder,
  flattened,
  sortMode,
  onSortMode,
  statusFilter,
  onStatusFilter,
  fuente,
  mes,
  periodYear,
  puedeAgrupar,
  onClearFuente,
  onClearMes,
}: {
  search: string
  onSearch: (value: string) => void
  order: PlanOrder
  onOrder: (value: PlanOrder) => void
  flattened: boolean
  /** Un filtro de la gráfica aplana la lista, así que «Agrupado» deja de ofrecerse. */
  puedeAgrupar: boolean
  sortMode: PlanSortMode
  onSortMode: (value: PlanSortMode) => void
  statusFilter: PlanStatusFilter
  onStatusFilter: (value: PlanStatusFilter) => void
  fuente: FuentePlan | null
  mes: number | null
  periodYear: number
  onClearFuente: () => void
  onClearMes: () => void
}) {
  const {t} = useTranslation()
  return (
    <div className="surface flex flex-wrap items-center gap-2.5 px-4 py-3">
      <Input
        maxLength={tope('BUSCA')}
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={t('plan.searchPlaceholder')}
        className="max-w-xs"
        data-testid="plan-search-input"
      />
      {fuente && (
        <FiltroChip
          label={t('plan.mando.fuenteChip', {fuente: t(`plan.resumen.fuente.${fuente}`)})}
          onClear={onClearFuente}
          testId="plan-filtro-fuente"
        />
      )}
      {mes !== null && (
        <FiltroChip
          label={t('plan.mando.mesChip', {mes: formatMonthName(periodYear, mes)})}
          onClear={onClearMes}
          testId="plan-filtro-mes"
        />
      )}
      <span className="flex-1" />
      <SegmentedControl
        testId="plan-order-toggle"
        size="sm"
        value={order}
        onChange={onOrder}
        options={(puedeAgrupar
          ? (['agrupado', 'target_date', 'status'] as const)
          : (['target_date', 'status'] as const)
        ).map((mode) => ({key: mode, label: t(`plan.order.${ORDER_LABEL_KEY[mode]}`)}))}
      />
      {!flattened && (
        <SegmentedControl
          testId="plan-sort-toggle"
          size="sm"
          value={sortMode}
          onChange={onSortMode}
          options={(['estandar', 'riesgo'] as const).map((mode) => ({
            key: mode,
            label: t(`plan.sort.${mode}`),
          }))}
        />
      )}
      <SegmentedControl
        testId="plan-status-filter"
        size="sm"
        value={statusFilter}
        onChange={onStatusFilter}
        options={(['todas', 'pendientes'] as const).map((mode) => ({
          key: mode,
          label: t(`plan.filter.${mode}`),
        }))}
      />
    </div>
  )
}

export function PlanCifras({done, total, exposicion}: {done: number; total: number; exposicion: Cop | null}) {
  const {t} = useTranslation()
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <StatCard
        label={t('plan.stats.avance')}
        value={`${done} / ${total}`}
        pct={(done / total) * 100}
        detail={<span data-testid="plan-progress">{t('plan.progress', {done, total})}</span>}
      />
      {exposicion && (
        <StatCard
          testId="plan-exposure-total"
          label={t('plan.stats.exposicion')}
          value={formatCopRangeOf(exposicion)}
          detail={t('plan.stats.exposicionHint')}
        />
      )}
    </div>
  )
}

export const ITEM_TONE: Record<string, 'green' | 'red' | 'neutral'> = {
  cumple: 'green',
  no_cumple: 'red',
  no_aplica: 'neutral',
}

export const ORDER_SORT_DIR: Record<Exclude<PlanOrder, 'agrupado'>, 'asc' | 'desc'> = {
  target_date: 'asc',
  status: 'desc',
}

export function parsePlanTab(value: string | null): 'plan' | 'resumen' {
  return value === 'resumen' ? 'resumen' : 'plan'
}
